import React, { createContext, useContext, useCallback, useRef, useEffect, ReactNode, useState } from 'react';
import { 
  MouseMovement, 
  ScrollEvent, 
  FocusEvent, 
  ClickEvent, 
  HesitationEvent,
  CognitiveLoadEvent,
  ProcessingModeEvent,
  MouseTrajectory
} from '../types';
import { classifyProcessingMode, FTTClassification } from '../utils/fttClassifier';
import { preprocessMouseTrajectory } from '../utils/mousePreprocessing';
import experimentTracker from '../utils/experimentTracking';

interface TrackerContextType {
  trackMouseMovement: (event: React.MouseEvent) => void;
  trackScroll: (event: React.UIEvent) => void;
  trackFocus: (element: string, type: 'focus' | 'blur') => void;
  trackClick: (element: string, position: { x: number; y: number }, metadata?: any) => void;
  trackHesitation: (element: string, type: 'hover' | 'movement') => void;
  trackCognitiveLoad: (level: 'low' | 'medium' | 'high', indicators: string[]) => void;
  trackProcessingMode: (emailId: string, responseTime: number) => void;
  trackHover: (element: string, type: 'enter' | 'leave', metadata?: any) => void;
  trackEmailInteraction: (emailId: string, action: 'view' | 'open' | 'close' | 're-read' | 'delete' | 'archive' | 'reportSpam' | 'reply' | 'replyAll' | 'forward') => void;
  getTrackingData: () => any;
  tracker: {
    track: (eventType: string, data: any) => void;
    trackMouseMovement: (event: React.MouseEvent) => void;
    events: any[];
  };
  fttClassifications: Map<string, FTTClassification>;
  classifyCurrentResponse: (responseId: string, responseTime: number, context: any) => FTTClassification;
}

const TrackerContext = createContext<TrackerContextType | undefined>(undefined);

export const useTracker = () => {
  const context = useContext(TrackerContext);
  if (!context) {
    // In development, return a mock context instead of throwing
    if (process.env.NODE_ENV === 'development') {
      console.warn('useTracker: No TrackerProvider found, returning mock context');
      return {
        trackMouseMovement: () => {},
        trackScroll: () => {},
        trackFocus: () => {},
        trackClick: () => {},
        trackHesitation: () => {},
        trackCognitiveLoad: () => {},
        trackProcessingMode: () => {},
        trackHover: () => {},
        trackEmailInteraction: () => {},
        getTrackingData: () => ({}),
        tracker: {
          track: () => {},
          trackMouseMovement: () => {},
          events: []
        },
        participantId: 'dev-mock',
        fttClassifications: new Map(),
        classifyCurrentResponse: () => ({ mode: 'gist', confidence: 0.5, features: {} } as any)
      } as unknown as TrackerContextType;
    }
    throw new Error('useTracker must be used within TrackerProvider');
  }
  return context;
};

interface TrackerProviderProps {
  children: ReactNode;
}

export const TrackerProvider: React.FC<TrackerProviderProps> = ({ children }) => {
  const trackingData = useRef({
    mouseMovements: [] as MouseMovement[],
    scrollEvents: [] as ScrollEvent[],
    focusEvents: [] as FocusEvent[],
    clicks: [] as ClickEvent[],
    hesitations: [] as HesitationEvent[],
    cognitiveLoad: [] as CognitiveLoadEvent[],
    processingMode: [] as ProcessingModeEvent[],
    events: [] as any[],
    mouseTrajectories: [] as any[]
  });

  const lastMousePosition = useRef({ x: 0, y: 0, timestamp: 0 });
  const hesitationTimer = useRef<NodeJS.Timeout | null>(null);
  const focusStartTime = useRef<{ [key: string]: number }>({});
  const eventBatch = useRef<any[]>([]);
  const batchTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Mouse tracking optimization
  const mouseTrackingBuffer = useRef<any[]>([]);
  const lastMouseSendTime = useRef(0);
  const mouseVelocityHistory = useRef<number[]>([]);
  const mouseAccelerationHistory = useRef<number[]>([]);
  
  // FTT classification state
  const [fttClassifications, setFttClassifications] = useState<Map<string, FTTClassification>>(new Map());
  const currentTrajectoryBuffer = useRef<MouseTrajectory[]>([]);
  const lastVelocity = useRef<number | null>(null);

  const sendBatch = useCallback(async () => {
    if (eventBatch.current.length === 0) return;
    
    const participantId = localStorage.getItem('participantId');
    if (!participantId) {
      console.error('No participant ID found');
      return;
    }
    
    const eventsToSend = [...eventBatch.current];
    eventBatch.current = [];
    
    console.log(`[TrackerContext] Sending batch of ${eventsToSend.length} events`);
    eventsToSend.forEach((e, i) => {
      if (e.eventType === 'emailDecision' || e.eventType === 'microSurveyResponse') {
        console.log(`  ${i + 1}. ${e.eventType}:`, e.data);
      }
    });
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/tracking/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          participantId,
          events: eventsToSend
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send tracking data');
      }
      
      console.log(`[TrackerContext] Successfully sent ${eventsToSend.length} tracking events`);
    } catch (error) {
      console.error('Error sending tracking data:', error);
      // Store failed batch in localStorage for retry with limits
      try {
        const failedBatches = JSON.parse(localStorage.getItem('failedTrackingBatches') || '[]');
        
        // Only keep the most recent 10 failed batches to prevent quota exceeded
        const limitedBatches = failedBatches.slice(-9); // Keep 9, add 1 new = 10 total
        limitedBatches.push({ timestamp: Date.now(), events: eventsToSend });
        
        localStorage.setItem('failedTrackingBatches', JSON.stringify(limitedBatches));
      } catch (storageError) {
        console.error('Failed to store failed batch - localStorage quota exceeded, clearing old batches');
        // Clear all failed batches if we can't store
        localStorage.removeItem('failedTrackingBatches');
      }
    }
  }, []);

  const track = useCallback((eventType: string, data: any) => {
    // Debug logging for email-related events
    if (eventType === 'emailDecision' || eventType === 'microSurveyResponse') {
      console.log(`[TrackerContext] Tracking ${eventType}:`, data);
    }
    
    const event = {
      // Stable per-event id so the backend can dedupe on batch retry.
      eventId: (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      eventType,
      timestamp: Date.now(),
      data
    };
    trackingData.current.events.push(event);
    eventBatch.current.push(event);
    
    // Clear existing timer
    if (batchTimer.current) {
      clearTimeout(batchTimer.current);
    }
    
    // Send batch if it's getting large or set timer for delayed send
    // For critical events, send immediately
    const criticalEvents = ['emailDecision', 'microSurveyResponse', 'deviceAction'];
    if (eventBatch.current.length >= 50 || criticalEvents.includes(eventType)) {
      console.log(`[TrackerContext] Sending immediately due to ${criticalEvents.includes(eventType) ? 'critical event' : 'batch size'}`);
      sendBatch();
    } else {
      batchTimer.current = setTimeout(sendBatch, 5000);
    }
  }, [sendBatch]);

  const trackMouseMovement = useCallback((event: React.MouseEvent) => {
    const now = Date.now();
    const { clientX: x, clientY: y } = event;
    const lastPos = lastMousePosition.current;

    // Throttle to ~33 Hz. Raw mousemove fires 60+/s; running the full jitter calc
    // + DOM closest() traversal on every event is needless client CPU and inflates
    // the buffers. ~30 ms sampling preserves trajectory fidelity for FTT analysis.
    if (lastPos.timestamp && now - lastPos.timestamp < 30) return;

    const timeDiff = now - lastPos.timestamp;
    const distance = Math.sqrt(Math.pow(x - lastPos.x, 2) + Math.pow(y - lastPos.y, 2));
    const velocity = timeDiff > 0 ? distance / timeDiff : 0;
    const acceleration = timeDiff > 0 ? velocity / timeDiff : 0;
    
    // Get element details for better analysis
    const target = event.target as HTMLElement;
    const elementInfo = {
      tagName: target.tagName,
      className: target.className,
      id: target.id,
      isLink: target.tagName === 'A' || target.closest('a') !== null,
      isButton: target.tagName === 'BUTTON' || target.closest('button') !== null,
      isEmail: target.closest('.email-item') !== null,
      emailId: target.closest('.email-item')?.getAttribute('data-email-id') || null
    };
    
    // Track velocity and acceleration history for jitter analysis
    mouseVelocityHistory.current.push(velocity);
    mouseAccelerationHistory.current.push(acceleration);
    
    // Keep only last 50 samples for rolling calculations
    if (mouseVelocityHistory.current.length > 50) {
      mouseVelocityHistory.current.shift();
      mouseAccelerationHistory.current.shift();
    }
    
    // Calculate additional metrics for R analysis
    const jitter = mouseVelocityHistory.current.length > 2 
      ? Math.sqrt(mouseVelocityHistory.current.slice(-10).reduce((sum, v, i, arr) => {
          if (i === 0) return sum;
          return sum + Math.pow(v - arr[i - 1], 2);
        }, 0) / (mouseVelocityHistory.current.slice(-10).length - 1))
      : 0;
    
    const avgVelocity = mouseVelocityHistory.current.length > 0
      ? mouseVelocityHistory.current.reduce((a, b) => a + b, 0) / mouseVelocityHistory.current.length
      : 0;
    
    // Direction change detection
    const angle = lastPos.x !== 0 || lastPos.y !== 0 
      ? Math.atan2(y - lastPos.y, x - lastPos.x) * 180 / Math.PI
      : 0;

    const movement: MouseMovement = {
      timestamp: now,
      x,
      y,
      velocity,
      acceleration,
      element: target.tagName
    };

    trackingData.current.mouseMovements.push(movement);
    
    // Add to FTT trajectory buffer
    currentTrajectoryBuffer.current.push({
      x,
      y,
      velocity,
      acceleration,
      angle,
      jitter,
      timestamp: now
    });
    
    // Trimmed movement data for backend (10 fields instead of 20)
    const elementType = elementInfo.isEmail ? 'email'
      : elementInfo.isButton ? 'button'
      : elementInfo.isLink ? 'link'
      : 'other';

    const enhancedMovementData = {
      clientX: event.clientX,
      clientY: event.clientY,
      velocity,
      acceleration,
      distance,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      elementType,
      emailId: elementInfo.emailId || null,
      // Keep element object for backend backward-compat parsing
      element: elementInfo
    };
    
    // Buffer movements and send in intelligent batches
    mouseTrackingBuffer.current.push(enhancedMovementData);
    
    // Send mouse data intelligently:
    // 1. Every 100ms if there's continuous movement
    // 2. When buffer reaches 20 movements
    // 3. On significant events (hover on links, sudden stops)
    const shouldSend = 
      now - lastMouseSendTime.current > 5000 || // Send every 5 seconds instead of 100ms
      mouseTrackingBuffer.current.length >= 200 || // Buffer 200 movements instead of 20
      (velocity < 0.1 && lastPos.timestamp > 0 && mouseTrackingBuffer.current.length > 50) || // Sudden stop with significant data
      (elementInfo.isLink && mouseTrackingBuffer.current.length > 20) || // Hovering over link with some data
      (elementInfo.isButton && mouseTrackingBuffer.current.length > 20); // Hovering over button with some data
    
    if (shouldSend && mouseTrackingBuffer.current.length > 0) {
      // Send aggregated mouse data
      track('mouseMove', {
        movements: mouseTrackingBuffer.current,
        summary: {
          count: mouseTrackingBuffer.current.length,
          duration: now - (mouseTrackingBuffer.current[0]?.timestamp || now),
          totalDistance: mouseTrackingBuffer.current.reduce((sum, m) => sum + (m.distance || 0), 0),
          avgVelocity: mouseTrackingBuffer.current.reduce((sum, m) => sum + m.velocity, 0) / mouseTrackingBuffer.current.length,
          maxVelocity: Math.max(...mouseTrackingBuffer.current.map(m => m.velocity)),
          minVelocity: Math.min(...mouseTrackingBuffer.current.map(m => m.velocity)),
          jitterScore: jitter,
          directionChanges: mouseTrackingBuffer.current.filter((m, i) => 
            i > 0 && Math.abs(m.angle - mouseTrackingBuffer.current[i - 1].angle) > 45
          ).length
        }
      });
      
      mouseTrackingBuffer.current = [];
      lastMouseSendTime.current = now;
    }
    
    lastMousePosition.current = { x, y, timestamp: now };

    // Detect hesitation based on low velocity
    if (velocity < 0.1 && velocity > 0) {
      if (!hesitationTimer.current) {
        hesitationTimer.current = setTimeout(() => {
          track('hesitation', {
            duration: 500,
            element: elementInfo,
            type: 'movement',
            position: { x, y },
            context: {
              avgVelocityBefore: avgVelocity,
              jitter: jitter
            }
          });
          hesitationTimer.current = null;
        }, 500);
      }
    } else if (hesitationTimer.current) {
      clearTimeout(hesitationTimer.current);
      hesitationTimer.current = null;
    }
  }, [track]);

  const scrollHistory = useRef<{ position: number; timestamp: number }[]>([]);
  const scrollPauseTimer = useRef<NodeJS.Timeout | null>(null);
  
  const trackScroll = useCallback((event: React.UIEvent) => {
    const target = event.target as HTMLElement;
    const now = Date.now();
    const currentPosition = target.scrollTop;
    const lastScroll = scrollHistory.current[scrollHistory.current.length - 1];
    
    // Calculate scroll metrics
    const timeDiff = lastScroll ? now - lastScroll.timestamp : 0;
    const positionDiff = lastScroll ? currentPosition - lastScroll.position : 0;
    const velocity = timeDiff > 0 ? Math.abs(positionDiff) / timeDiff : 0;
    const direction: 'up' | 'down' = positionDiff >= 0 ? 'down' : 'up';
    
    // Track scroll history
    scrollHistory.current.push({ position: currentPosition, timestamp: now });
    if (scrollHistory.current.length > 50) scrollHistory.current.shift();
    
    // Detect direction changes
    const directionChanges = scrollHistory.current.slice(-10).reduce((count, scroll, i, arr) => {
      if (i === 0) return count;
      const prevDir = arr[i-1].position > arr[i-2]?.position ? 'down' : 'up';
      const currDir = scroll.position > arr[i-1].position ? 'down' : 'up';
      return prevDir !== currDir ? count + 1 : count;
    }, 0);
    
    const scrollEvent: ScrollEvent = {
      timestamp: now,
      position: currentPosition,
      direction,
      velocity
    };
    trackingData.current.scrollEvents.push(scrollEvent);
    
    // Detect scroll pauses (potential reading)
    if (scrollPauseTimer.current) {
      clearTimeout(scrollPauseTimer.current);
    }
    
    scrollPauseTimer.current = setTimeout(() => {
      // User paused scrolling - likely reading
      const viewportHeight = target.clientHeight;
      const scrollPercentage = (currentPosition / (target.scrollHeight - viewportHeight)) * 100;
      
      track('scrollPause', {
        position: currentPosition,
        scrollPercentage,
        pauseDuration: 500,
        viewportHeight,
        totalHeight: target.scrollHeight,
        visibleEmails: Array.from(target.querySelectorAll('.email-item')).filter(el => {
          const rect = el.getBoundingClientRect();
          return rect.top < window.innerHeight && rect.bottom > 0;
        }).map(el => el.getAttribute('data-email-id'))
      });
    }, 500);
    
    // Send scroll data in batches
    if (scrollHistory.current.length % 10 === 0) {
      track('scroll', {
        metrics: {
          totalDistance: scrollHistory.current.reduce((sum, s, i) => 
            i === 0 ? sum : sum + Math.abs(s.position - scrollHistory.current[i-1].position), 0),
          avgVelocity: velocity,
          directionChanges,
          scrollPattern: scrollHistory.current.slice(-10).map(s => ({
            position: s.position,
            timestamp: s.timestamp
          }))
        }
      });
    }
  }, [track]);

  const trackFocus = useCallback((element: string, type: 'focus' | 'blur') => {
    const now = Date.now();
    const focusEvent: FocusEvent = {
      timestamp: now,
      type,
      element,
      duration: type === 'blur' ? now - (focusStartTime.current[element] || now) : undefined
    };

    if (type === 'focus') {
      focusStartTime.current[element] = now;
    } else {
      delete focusStartTime.current[element];
    }

    trackingData.current.focusEvents.push(focusEvent);
  }, []);

  const trackClick = useCallback((element: string, position: { x: number; y: number }, metadata?: any) => {
    const clickEvent: ClickEvent = {
      timestamp: Date.now(),
      element,
      position,
      ...metadata
    };
    trackingData.current.clicks.push(clickEvent);
  }, []);

  const trackHesitation = useCallback((element: string, type: 'hover' | 'movement') => {
    const hesitationEvent: HesitationEvent = {
      timestamp: Date.now(),
      duration: 0, // Would need to track start time
      element,
      type
    };
    trackingData.current.hesitations.push(hesitationEvent);
    
    // Send hesitation event to backend
    track('hesitation', {
      duration: 0,
      element,
      type
    });
  }, [track]);

  const trackCognitiveLoad = useCallback((level: 'low' | 'medium' | 'high', indicators: string[]) => {
    const cognitiveLoadEvent: CognitiveLoadEvent = {
      timestamp: Date.now(),
      level,
      indicators
    };
    trackingData.current.cognitiveLoad.push(cognitiveLoadEvent);
  }, []);

  const trackProcessingMode = useCallback((emailId: string, responseTime: number) => {
    // Determine processing mode based on various factors
    const quickResponse = responseTime < 2000; // Less than 2 seconds
    const hasHesitations = trackingData.current.hesitations.length > 0;
    const mouseVelocity = trackingData.current.mouseMovements.slice(-10).reduce((sum, m) => sum + m.velocity, 0) / 10;
    
    const mode = quickResponse && !hasHesitations && mouseVelocity > 0.5 ? 'automatic' : 'deliberate';
    
    // Count hesitations in the last 5 seconds
    const recentHesitations = trackingData.current.hesitations.filter(
      h => h.timestamp > Date.now() - 5000
    ).length;
    
    const processingModeEvent: ProcessingModeEvent = {
      timestamp: Date.now(),
      emailId,
      mode,
      responseTime,
      indicators: [
        quickResponse ? 'quickResponse' : 'slowResponse',
        hasHesitations ? 'hasHesitations' : 'noHesitations',
        mouseVelocity > 0.5 ? 'highVelocity' : 'lowVelocity',
        recentHesitations > 2 ? 'multipleHesitations' : 'fewHesitations'
      ]
    };
    trackingData.current.processingMode.push(processingModeEvent);
  }, []);
  
  // Hover tracking
  const hoverStartTimes = useRef<{ [key: string]: number }>({});
  
  const trackHover = useCallback((element: string, type: 'enter' | 'leave', metadata?: any) => {
    const now = Date.now();
    
    if (type === 'enter') {
      hoverStartTimes.current[element] = now;
      track('hoverStart', {
        element,
        timestamp: now,
        ...metadata
      });
    } else {
      const startTime = hoverStartTimes.current[element];
      const duration = startTime ? now - startTime : 0;
      delete hoverStartTimes.current[element];
      
      track('hoverEnd', {
        element,
        timestamp: now,
        duration,
        ...metadata
      });
      
      // Track significant hovers (>500ms)
      if (duration > 500) {
        track('significantHover', {
          element,
          duration,
          ...metadata
        });
      }
    }
  }, [track]);
  
  // Email interaction sequence tracking
  const emailInteractionHistory = useRef<{ emailId: string; action: string; timestamp: number }[]>([]);
  const emailViewTimes = useRef<{ [emailId: string]: number }>({});
  
  const trackEmailInteraction = useCallback((emailId: string, action: 'view' | 'open' | 'close' | 're-read' | 'delete' | 'archive' | 'reportSpam' | 'reply' | 'replyAll' | 'forward') => {
    const now = Date.now();
    
    // Track view time
    if (action === 'view' || action === 'open') {
      emailViewTimes.current[emailId] = now;
    } else if (action === 'close' && emailViewTimes.current[emailId]) {
      const viewDuration = now - emailViewTimes.current[emailId];
      track('emailViewTime', {
        emailId,
        duration: viewDuration
      });
    }
    
    // Check for re-reading pattern
    const previousInteraction = emailInteractionHistory.current.find(
      i => i.emailId === emailId && (i.action === 'view' || i.action === 'open')
    );
    
    if (previousInteraction && (action === 'view' || action === 'open')) {
      action = 're-read';
    }
    
    // Add to history
    emailInteractionHistory.current.push({ emailId, action, timestamp: now });
    
    // Keep only last 50 interactions
    if (emailInteractionHistory.current.length > 50) {
      emailInteractionHistory.current.shift();
    }
    
    // Track the interaction
    track('emailInteraction', {
      emailId,
      action,
      sequenceIndex: emailInteractionHistory.current.length - 1,
      timeSinceLastInteraction: emailInteractionHistory.current.length > 1 
        ? now - emailInteractionHistory.current[emailInteractionHistory.current.length - 2].timestamp
        : 0,
      interactionPattern: emailInteractionHistory.current.slice(-5).map(i => ({
        emailId: i.emailId,
        action: i.action
      }))
    });
  }, [track]);

  const getTrackingData = useCallback(() => {
    return { ...trackingData.current };
  }, []);

  const tracker = {
    track,
    trackMouseMovement,
    events: trackingData.current.events
  };

  // Send any remaining events when component unmounts
  useEffect(() => {
    return () => {
      if (batchTimer.current) {
        clearTimeout(batchTimer.current);
      }
      if (eventBatch.current.length > 0) {
        sendBatch();
      }
    };
  }, [sendBatch]);
  
  // Retry failed batches on mount
  useEffect(() => {
    const retryFailedBatches = async () => {
      try {
        const failedBatches: Array<{ timestamp: number; events: any[] }> = JSON.parse(
          localStorage.getItem('failedTrackingBatches') || '[]'
        );
        if (failedBatches.length > 0) {
          console.log(`Retrying ${failedBatches.length} failed tracking batches`);
          
          // Only retry recent batches (last 24 hours) to avoid overwhelming the server
          const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
          const recentBatches = failedBatches.filter(batch => batch.timestamp > oneDayAgo);
          
          for (const batch of recentBatches.slice(0, 5)) { // Limit to 5 retries max
            eventBatch.current = batch.events;
            await sendBatch();
          }
          
          localStorage.removeItem('failedTrackingBatches');
        }
      } catch (error) {
        console.error('Error retrying failed batches:', error);
        // Clear corrupted data
        localStorage.removeItem('failedTrackingBatches');
      }
    };
    retryFailedBatches();
  }, [sendBatch]);

  const classifyCurrentResponse = useCallback((responseId: string, responseTime: number, context: any) => {
    const trajectory = [...currentTrajectoryBuffer.current];
    // context.fttConfig (optional) lets a task override classifier tuning constants
    // — e.g. info-sharing can set a larger temporal.center for long reading times.
    const classification = classifyProcessingMode(responseTime, trajectory, context, context?.fttConfig || {});
    
    setFttClassifications(prev => new Map(prev).set(responseId, classification));
    
    // Store with event
    track('fttClassification', {
      responseId,
      classification,
      trajectoryLength: trajectory.length,
      timestamp: Date.now()
    });
    
    // Process and send mouse trajectory data. Threshold lowered from 10 → 3 so a
    // low-movement reader (e.g. someone who barely moves the mouse while reading a
    // news article) still yields a stored trajectory instead of being silently
    // dropped — this was why one of four info-sharing articles had no trajectory.
    // preprocessMouseTrajectory still returns null for degenerate input, so the
    // guard below prevents storing garbage.
    if (trajectory.length > 3) {
      const rawTrajectory = trajectory.map((point, index) => ({
        x: point.x,
        y: point.y,
        timestamp: point.timestamp
      }));
      
      const preprocessedTrajectory = preprocessMouseTrajectory(rawTrajectory);
      
      if (preprocessedTrajectory) {
        // Calculate additional metrics for the trajectory
        const metrics = {
          originalSamplingRate: trajectory.length > 1 
            ? 1000 / ((trajectory[trajectory.length - 1].timestamp - trajectory[0].timestamp) / trajectory.length)
            : 0,
          avgVelocity: trajectory.length > 0 ? trajectory.reduce((sum, p) => sum + (p.velocity || 0), 0) / trajectory.length : 0,
          maxVelocity: trajectory.length > 0 ? Math.max(...trajectory.map(p => p.velocity || 0)) : 0,
          avgAcceleration: trajectory.length > 0 ? trajectory.reduce((sum, p) => sum + (p.acceleration || 0), 0) / trajectory.length : 0,
          maxAcceleration: trajectory.length > 0 ? Math.max(...trajectory.map(p => p.acceleration || 0)) : 0,
          pauseCount: trajectory.filter(p => (p.velocity || 0) < 0.1).length,
          trajectoryComplexity: trajectory.filter((p, i) => 
            i > 0 && Math.abs((p.angle || 0) - (trajectory[i-1].angle || 0)) > 30
          ).length,
          totalDistance: trajectory.reduce((sum, p, i) => {
            if (i === 0) return 0;
            const dx = p.x - trajectory[i-1].x;
            const dy = p.y - trajectory[i-1].y;
            return sum + Math.sqrt(dx * dx + dy * dy);
          }, 0),
          directDistance: trajectory.length > 1 
            ? Math.sqrt(
                Math.pow(trajectory[trajectory.length-1].x - trajectory[0].x, 2) + 
                Math.pow(trajectory[trajectory.length-1].y - trajectory[0].y, 2)
              )
            : 0
        };
        
        track('preprocessedMouseTrajectory', {
          taskId: responseId,
          taskType: context.taskType || 'unknown',
          trajectory: preprocessedTrajectory,
          metrics
        });
      }
    }
    
    // Track learning control data
    if (context.scenarioIndex !== undefined && context.taskType) {
      experimentTracker.trackScenarioAttempt(
        localStorage.getItem('participantId') || '',
        context.taskType,
        context.scenarioIndex,
        { 
          responseId, 
          responseTime,
          classification: classification.mode 
        },
        context.conditions || {}
      );
    }
    
    // Clear buffer for next response
    currentTrajectoryBuffer.current = [];
    
    return classification;
  }, [track]);

  const contextValue: TrackerContextType = {
    trackMouseMovement,
    trackScroll,
    trackFocus,
    trackClick,
    trackHesitation,
    trackCognitiveLoad,
    trackProcessingMode,
    trackHover,
    trackEmailInteraction,
    getTrackingData,
    tracker,
    fttClassifications,
    classifyCurrentResponse
  };

  return (
    <TrackerContext.Provider value={contextValue}>
      {children}
    </TrackerContext.Provider>
  );
};